import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'
import { RefreshCw, Plus, Trash2, X, Upload, CheckCircle, FileText, Edit } from 'lucide-react'
import Modal from './Modal'
import DOMPurify from 'dompurify'

// Shared modal overlay style
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
}

// Shared modal container base style
const modalContainerBase = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '12px',
  padding: '24px',
  width: '90%'
}

// Shared close button style
const modalCloseButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#9ca3af',
  cursor: 'pointer',
  padding: '4px'
}

/**
 * Safely converts Markdown-like syntax to HTML and sanitizes it to prevent XSS attacks
 * @param {string} markdown - The markdown text to convert
 * @returns {string} - Sanitized HTML string
 */
const convertMarkdownToSafeHTML = (markdown) => {
  if (!markdown) return ''

  // Convert markdown syntax to HTML
  const html = markdown
    .replaceAll(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replaceAll(/\*(.*?)\*/g, '<em>$1</em>')
    .replaceAll(/`(.*?)`/g, '<code style="background-color: #374151; padding: 2px 4px; border-radius: 3px;">$1</code>')
    .replaceAll(/^## (.*$)/gim, '<h2 style="font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; color: #f3f4f6;">$1</h2>')
    .replaceAll(/^# (.*$)/gim, '<h1 style="font-size: 22px; font-weight: 700; margin-top: 20px; margin-bottom: 10px; color: #f3f4f6;">$1</h1>')
    .replaceAll(/^- (.*$)/gim, '<li style="margin-left: 20px;">$1</li>')
    .replaceAll('\n', '<br>')

  // Sanitize HTML to prevent XSS attacks
  // Only allow safe tags and the style attribute for formatting
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'code', 'h1', 'h2', 'li', 'br'],
    ALLOWED_ATTR: ['style'],
    ALLOW_DATA_ATTR: false
  })
}

/**
 * Download Administration Component for Admin Dashboard
 * Shows only SoftwareProducts that already have downloads
 */
function DownloadAdministrationContent() {
  const [products, setProducts] = useState([])
  const [availableProducts, setAvailableProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadForm, setUploadForm] = useState({
    software_product_id: '',
    channel: 'stable',
    version: '',
    os: 'windows',
    arch: 'x64',
    file: null,
    beta_plan_id: ''
  })
  const [uploadError, setUploadError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [availablePlans, setAvailablePlans] = useState([])
  const [loadingPlans, setLoadingPlans] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch products that already have downloads
      const downloadsRes = await apiFetch('/api/downloads/products')
      const downloadsData = await downloadsRes.json()

      if (downloadsRes.ok) {
        setProducts(downloadsData.products || [])
      } else {
        setError(downloadsData.error || 'Failed to fetch products')
        setLoading(false)
        return
      }

      // Fetch available software products for upload modal
      const softwareRes = await apiFetch('/api/admin/downloads/software-products')
      const softwareData = await softwareRes.json()

      if (softwareRes.ok) {
        setAvailableProducts(softwareData.products || [])
      }

    } catch (err) {
      setError('Network error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchPlansForProduct = async (productId) => {
    if (!productId) {
      setAvailablePlans([])
      return
    }

    try {
      setLoadingPlans(true)
      const res = await apiFetch(`/api/admin/software-products/${productId}/plans`)
      const data = await res.json()

      if (res.ok) {
        // Extract plan objects from the response
        const plans = (data.plans || []).map(item => item.plan)
        setAvailablePlans(plans)
      } else {
        setAvailablePlans([])
      }
    } catch (err) {
      console.error('Error fetching plans:', err)
      setAvailablePlans([])
    } finally {
      setLoadingPlans(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadForm(prev => ({ ...prev, file }))
    }
  }

  const handleProductChange = (e) => {
    const productId = e.target.value
    setUploadForm(prev => ({ ...prev, software_product_id: productId, beta_plan_id: '' }))
    if (uploadForm.channel === 'beta') {
      fetchPlansForProduct(productId)
    }
  }

  const handleChannelChange = (e) => {
    const channel = e.target.value
    setUploadForm(prev => ({ ...prev, channel, beta_plan_id: '' }))
    if (channel === 'beta' && uploadForm.software_product_id) {
      fetchPlansForProduct(uploadForm.software_product_id)
    } else {
      setAvailablePlans([])
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer?.files?.[0]) {
      setUploadForm(prev => ({ ...prev, file: e.dataTransfer.files[0] }))
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    setUploadError(null)

    if (!uploadForm.software_product_id || !uploadForm.version || !uploadForm.file) {
      setUploadError('Bitte füllen Sie alle Felder aus')
      return
    }

    if (uploadForm.channel === 'beta' && !uploadForm.beta_plan_id) {
      setUploadError('Bitte wählen Sie einen Beta-Plan aus')
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('software_product_id', uploadForm.software_product_id)
      formData.append('channel', uploadForm.channel)
      formData.append('version', uploadForm.version)
      formData.append('os', uploadForm.os)
      formData.append('arch', uploadForm.arch)
      formData.append('file', uploadForm.file)
      if (uploadForm.channel === 'beta' && uploadForm.beta_plan_id) {
        formData.append('beta_plan_id', uploadForm.beta_plan_id)
      }

      const res = await apiFetch('/api/admin/downloads/upload', {
        method: 'POST',
        body: formData
      })

      const data = await res.json()

      if (res.ok) {
        setShowUploadModal(false)
        setUploadForm({ software_product_id: '', channel: 'stable', version: '', os: 'windows', arch: 'x64', file: null, beta_plan_id: '' })
        setAvailablePlans([])
        fetchData()
      } else {
        setUploadError(data.error || 'Upload fehlgeschlagen')
      }
    } catch (err) {
      setUploadError('Network error: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div className="content-section">
        <h2>Download Administration</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          Loading products...
        </div>
      </div>
    )
  }

  return (
    <div className="content-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>Download Administration</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={fetchData}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={16} />
            Add Update
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {/* Products and Releases Table */}
      <div style={{
        backgroundColor: '#1f2937',
        border: '1px solid #374151',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {products.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            Keine Produkte mit Downloads gefunden. Verwenden Sie den "Add Update" Button, um das erste Release hochzuladen.
          </div>
        ) : (
          products.map(product => (
            <div key={product.id} style={{ borderBottom: '1px solid #374151' }}>
              {/* Product Header */}
              <div style={{
                padding: '16px',
                backgroundColor: '#111827',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {product.icon_url && (
                    <img
                      src={product.icon_url}
                      alt={product.name}
                      style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }}
                    />
                  )}
                  <div>
                    <div style={{ color: '#e5e7eb', fontWeight: '600', marginBottom: '4px' }}>{product.name}</div>
                    <code style={{
                      color: '#9ca3af',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      backgroundColor: '#1f2937',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {product.slug}
                    </code>
                  </div>
                </div>
              </div>

              {/* Releases List - Fetch and display per product */}
              <ProductReleases
                productSlug={product.slug}
              />
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={modalOverlayStyle}>
          <div style={{
            ...modalContainerBase,
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => {
                setShowUploadModal(false)
                setUploadForm({ software_product_id: '', channel: 'stable', version: '', os: 'windows', arch: 'x64', file: null, beta_plan_id: '' })
                setUploadError(null)
                setAvailablePlans([])
              }}
              style={{
                ...modalCloseButtonStyle,
                position: 'absolute',
                top: '16px',
                right: '16px'
              }}
            >
              <X size={20} />
            </button>

            <h3 style={{ color: '#e5e7eb', marginBottom: '24px' }}>Add Update</h3>

            {uploadError && (
              <div className="error-message" style={{ marginBottom: '16px' }}>
                {uploadError}
              </div>
            )}

            <form onSubmit={handleUpload}>
              {/* Software Product Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="upload-software-product" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Software Produkt *
                </label>
                <select
                  id="upload-software-product"
                  value={uploadForm.software_product_id}
                  onChange={handleProductChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb'
                  }}
                >
                  <option value="">Wählen Sie ein Produkt</option>
                  {availableProducts.map(product => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              </div>

              {/* Channel Selection */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="upload-channel" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Channel *
                </label>
                <select
                  id="upload-channel"
                  value={uploadForm.channel}
                  onChange={handleChannelChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb'
                  }}
                >
                  <option value="stable">Stable</option>
                  <option value="beta">Beta</option>
                </select>
              </div>

              {/* Beta Plan Selection (only for beta channel) */}
              {uploadForm.channel === 'beta' && (
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="upload-beta-plan" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                    Beta Plan *
                  </label>
                  {loadingPlans ? (
                    <div style={{ padding: '10px', color: '#9ca3af', textAlign: 'center' }}>
                      Lade Pläne...
                    </div>
                  ) : (
                    <select
                      id="upload-beta-plan"
                      value={uploadForm.beta_plan_id}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, beta_plan_id: e.target.value }))}
                      required
                      disabled={!uploadForm.software_product_id || availablePlans.length === 0}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '6px',
                        color: '#e5e7eb'
                      }}
                    >
                      <option value="">
                        {availablePlans.length === 0
                          ? 'Keine Pläne verfügbar'
                          : 'Wählen Sie einen Plan'}
                      </option>
                      {availablePlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} ({plan.billing_interval})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Version Input */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="upload-version" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Version *
                </label>
                <input
                  id="upload-version"
                  type="text"
                  value={uploadForm.version}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, version: e.target.value }))}
                  required
                  placeholder="z.B. 1.0.0 oder 1.1.0-beta1"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb'
                  }}
                />
              </div>

              {/* OS and Arch Selection */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label htmlFor="upload-os" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                    Betriebssystem *
                  </label>
                  <select
                    id="upload-os"
                    value={uploadForm.os}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, os: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '6px',
                      color: '#e5e7eb'
                    }}
                  >
                    <option value="windows">Windows</option>
                    <option value="macos">macOS</option>
                    <option value="linux">Linux</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="upload-arch" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                    Architektur *
                  </label>
                  <select
                    id="upload-arch"
                    value={uploadForm.arch}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, arch: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#111827',
                      border: '1px solid #374151',
                      borderRadius: '6px',
                      color: '#e5e7eb'
                    }}
                  >
                    <option value="x64">x64</option>
                    <option value="arm64">ARM64</option>
                  </select>
                </div>
              </div>

              {/* File Upload */}
              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="fileInput" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Datei *
                </label>
                <button
                  type="button"
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  style={{
                    border: `2px dashed ${dragActive ? '#3b82f6' : '#374151'}`,
                    borderRadius: '6px',
                    padding: '32px',
                    textAlign: 'center',
                    backgroundColor: dragActive ? '#1e3a8a' : '#111827',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    width: '100%',
                    font: 'inherit'
                  }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <Upload size={32} style={{ color: '#9ca3af', margin: '0 auto 12px' }} />
                  <p style={{ color: '#9ca3af', marginBottom: '8px' }}>
                    {uploadForm.file ? uploadForm.file.name : 'Datei hier ablegen oder klicken zum Auswählen'}
                  </p>
                  <p style={{ color: '#6b7280', fontSize: '12px' }}>
                    Alle Dateitypen erlaubt
                  </p>
                  <input
                    id="fileInput"
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </button>
              </div>

              {/* Submit Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={uploading}
                  className="btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  <Upload size={16} />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}

// Component to display releases for a product
function ProductReleases({ productSlug }) {
  const [releases, setReleases] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [publishData, setPublishData] = useState({ channel: '', version: '', releaseNotes: '' })
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [notesData, setNotesData] = useState({ channel: '', version: '', releaseNotes: '', isPublic: false })
  const [editingNotes, setEditingNotes] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editData, setEditData] = useState({ channel: '', version: '', os: '', arch: '', filename: '', productSlug: '' })

  useEffect(() => {
    fetchReleases()
  }, [productSlug])

  const fetchReleases = async () => {
    try {
      setLoading(true)
      const res = await apiFetch(`/api/downloads/products/${productSlug}`)
      const data = await res.json()

      if (res.ok) {
        // Combine stable and beta releases
        const allReleases = [
          ...(data.channels?.stable || []).map(r => ({ ...r, channel: 'stable' })),
          ...(data.channels?.beta || []).map(r => ({ ...r, channel: 'beta' }))
        ]
        setReleases(allReleases)
      }
    } catch (err) {
      console.error('Error fetching releases:', err)
    } finally {
      setLoading(false)
    }
  }

  const openPublishModal = (channel, version) => {
    setPublishData({ channel, version, releaseNotes: '' })
    setShowPublishModal(true)
  }

  const closePublishModal = () => {
    setShowPublishModal(false)
    setPublishData({ channel: '', version: '', releaseNotes: '' })
  }

  const handlePublish = async () => {
    try {
      const res = await apiFetch(`/api/admin/downloads/releases/${productSlug}/${publishData.channel}/${publishData.version}/publish`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          release_notes: publishData.releaseNotes
        })
      })

      if (res.ok) {
        setModalMessage('Release erfolgreich veröffentlicht!')
        setModalOpen(true)
        closePublishModal()
        fetchReleases()
      } else {
        let errorMsg = `HTTP ${res.status}: `
        try {
          const data = await res.json()
          errorMsg += data.error || 'Release konnte nicht veröffentlicht werden'
        } catch {
          // If response is not JSON, use a generic error message
          errorMsg += `Unerwarteter Fehler (keine JSON-Antwort)`
        }
        setModalMessage(`Fehler: ${errorMsg}`)
        setModalOpen(true)
      }
    } catch (err) {
      console.error('Error publishing release:', err)
      setModalMessage(`Fehler beim Veröffentlichen des Releases: ${err.message}`)
      setModalOpen(true)
    }
  }

  const openEditModal = (release) => {
    // Get first platform data for editing
    const platform = release.platforms?.[0]
    if (platform) {
      setEditData({
        channel: release.channel,
        version: release.version,
        os: platform.os,
        arch: platform.arch,
        filename: platform.filename,
        productSlug: productSlug
      })
      setShowEditModal(true)
    }
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditData({ channel: '', version: '', os: '', arch: '', filename: '', productSlug: '' })
  }

  const handleUpdateDownload = async () => {
    try {
      const res = await apiFetch(`/api/admin/downloads/releases/${editData.productSlug}/${editData.channel}/${editData.version}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel: editData.channel,
          os: editData.os,
          arch: editData.arch
        })
      })

      if (res.ok) {
        setModalMessage('Download erfolgreich aktualisiert!')
        setModalOpen(true)
        closeEditModal()
        fetchReleases()
      } else {
        const data = await res.json()
        setModalMessage(`Fehler: ${data.error || 'Download konnte nicht aktualisiert werden'}`)
        setModalOpen(true)
      }
    } catch (err) {
      console.error('Error updating download:', err)
      setModalMessage(`Fehler beim Aktualisieren des Downloads: ${err.message}`)
      setModalOpen(true)
    }
  }

  const openNotesModal = (release) => {
    setNotesData({
      channel: release.channel,
      version: release.version,
      releaseNotes: release.release_notes || '',
      isPublic: release.is_public
    })
    setEditingNotes(false)
    setShowNotesModal(true)
  }

  const closeNotesModal = () => {
    setShowNotesModal(false)
    setNotesData({ channel: '', version: '', releaseNotes: '', isPublic: false })
    setEditingNotes(false)
  }

  const handleSaveNotes = async () => {
    try {
      const res = await apiFetch(`/api/admin/downloads/releases/${productSlug}/${notesData.channel}/${notesData.version}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          release_notes: notesData.releaseNotes
        })
      })

      if (res.ok) {
        setModalMessage('Release Notes erfolgreich gespeichert!')
        setModalOpen(true)
        setEditingNotes(false)
        fetchReleases()
      } else {
        const data = await res.json()
        setModalMessage(`Fehler: ${data.error || 'Release Notes konnten nicht gespeichert werden'}`)
        setModalOpen(true)
      }
    } catch (err) {
      console.error('Error saving release notes:', err)
      setModalMessage(`Fehler beim Speichern der Release Notes: ${err.message}`)
      setModalOpen(true)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return

    const { channel, version } = deleteTarget

    try {
      const res = await apiFetch(`/api/admin/downloads/releases/${productSlug}/${channel}/${version}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        setShowDeleteConfirm(false)
        setDeleteTarget(null)
        fetchReleases()
        setModalMessage('Release erfolgreich gelöscht!')
        setModalOpen(true)
      } else {
        const data = await res.json()
        setShowDeleteConfirm(false)
        setDeleteTarget(null)
        setModalMessage('Fehler: ' + (data.error || 'Löschen fehlgeschlagen'))
        setModalOpen(true)
      }
    } catch (err) {
      setShowDeleteConfirm(false)
      setDeleteTarget(null)
      setModalMessage('Network error: ' + err.message)
      setModalOpen(true)
    }
  }

  if (loading) {
    return <div style={{ padding: '16px', color: '#9ca3af', textAlign: 'center' }}>Loading releases...</div>
  }

  if (releases.length === 0) {
    return <div style={{ padding: '16px', color: '#9ca3af', textAlign: 'center' }}>No releases found</div>
  }

  return (
    <div style={{ padding: '16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #374151' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>VERSION</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>CHANNEL</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>STATUS</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>RELEASE DATE</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>PLATFORMS</th>
            <th style={{ padding: '8px', textAlign: 'center', color: '#9ca3af', fontSize: '12px', fontWeight: '500' }}>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {releases.map(release => (
            <tr key={release.id} style={{ borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '12px' }}>
                <code style={{
                  color: '#e5e7eb',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  backgroundColor: '#111827',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}>
                  {release.version}
                </code>
              </td>
              <td style={{ padding: '12px' }}>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: release.channel === 'stable' ? '#065f46' : '#7c2d12',
                  color: release.channel === 'stable' ? '#d1fae5' : '#fed7aa',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {release.channel.charAt(0).toUpperCase() + release.channel.slice(1)}
                </span>
              </td>
              <td style={{ padding: '12px' }}>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: release.is_public ? '#065f46' : '#78350f',
                  color: release.is_public ? '#d1fae5' : '#fef3c7',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {release.is_public ? 'Public' : 'Private'}
                </span>
              </td>
              <td style={{ padding: '12px', color: '#9ca3af', fontSize: '14px' }}>
                {release.release_date}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {release.platforms?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {release.platforms.map((platform) => (
                      <span key={`${release.id}-${platform}`} style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: '#1f2937',
                        color: '#e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'monospace'
                      }}>
                        {platform.os}/{platform.arch}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#6b7280' }}>No platforms</span>
                )}
              </td>
              <td style={{ padding: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {!release.is_public && (
                    <button
                      onClick={() => openPublishModal(release.channel, release.version)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        backgroundColor: '#065f46',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      <CheckCircle size={14} />
                      Publish
                    </button>
                  )}
                  {release.platforms?.length > 0 && (
                    <button
                      onClick={() => openEditModal(release)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        backgroundColor: '#1e40af',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                  )}
                  {release.is_public && (
                    <button
                      onClick={() => openNotesModal(release)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        backgroundColor: release.release_notes ? '#7c2d12' : '#374151',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={release.release_notes ? 'Release Notes anzeigen/bearbeiten' : 'Keine Release Notes vorhanden'}
                    >
                      <FileText size={14} />
                      Notes
                    </button>
                  )}
                  {!release.is_public && (
                    <button
                      onClick={() => {
                        setDeleteTarget({ channel: release.channel, version: release.version })
                        setShowDeleteConfirm(true)
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        backgroundColor: '#991b1b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Publish Modal */}
      {showPublishModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContainerBase, maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#e5e7eb', margin: 0 }}>Release veröffentlichen</h3>
              <button onClick={closePublishModal} style={modalCloseButtonStyle}>
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>
                Version: <strong style={{ color: '#e5e7eb' }}>{publishData.version}</strong> ({publishData.channel})
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="publish-release-notes" style={{ display: 'block', marginBottom: '8px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                Release Notes (Markdown)
              </label>
              <textarea
                id="publish-release-notes"
                value={publishData.releaseNotes}
                onChange={(e) => setPublishData({ ...publishData, releaseNotes: e.target.value })}
                placeholder="## Was ist neu?&#10;&#10;- Feature 1&#10;- Feature 2&#10;&#10;## Fehlerbehebungen&#10;&#10;- Bug fix 1"
                style={{
                  width: '100%',
                  minHeight: '300px',
                  padding: '12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
              <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>
                Markdown-Formatierung wird unterstützt: **fett**, *kursiv*, `code`, ## Überschriften, - Listen
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closePublishModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#374151',
                  color: '#e5e7eb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handlePublish}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#065f46',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <CheckCircle size={16} />
                Jetzt veröffentlichen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Release Notes Modal */}
      {showNotesModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContainerBase, maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#e5e7eb', margin: 0 }}>
                Release Notes - {notesData.version} ({notesData.channel})
              </h3>
              <button onClick={closeNotesModal} style={modalCloseButtonStyle}>
                <X size={20} />
              </button>
            </div>

            {!editingNotes && notesData.releaseNotes && (
              <div style={{ marginBottom: '20px' }}>
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    minHeight: '200px',
                    whiteSpace: 'pre-wrap'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: convertMarkdownToSafeHTML(notesData.releaseNotes)
                  }}
                />
              </div>
            )}

            {!editingNotes && !notesData.releaseNotes && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6b7280',
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                <p>Keine Release Notes vorhanden</p>
              </div>
            )}

            {editingNotes && (
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="edit-release-notes" style={{ display: 'block', marginBottom: '8px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                  Release Notes bearbeiten (Markdown)
                </label>
                <textarea
                  id="edit-release-notes"
                  value={notesData.releaseNotes}
                  onChange={(e) => setNotesData({ ...notesData, releaseNotes: e.target.value })}
                  placeholder="## Was ist neu?&#10;&#10;- Feature 1&#10;- Feature 2&#10;&#10;## Fehlerbehebungen&#10;&#10;- Bug fix 1"
                  style={{
                    width: '100%',
                    minHeight: '400px',
                    padding: '12px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
                <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>
                  Markdown-Formatierung wird unterstützt: **fett**, *kursiv*, `code`, ## Überschriften, - Listen
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {!editingNotes && notesData.isPublic && (
                <>
                  <button
                    onClick={closeNotesModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#374151',
                      color: '#e5e7eb',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Schließen
                  </button>
                  <button
                    onClick={() => setEditingNotes(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#7c2d12',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    <Edit size={16} />
                    Bearbeiten
                  </button>
                </>
              )}
              {editingNotes && (
                <>
                  <button
                    onClick={() => setEditingNotes(false)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#374151',
                      color: '#e5e7eb',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#065f46',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    <CheckCircle size={16} />
                    Speichern
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteTarget && (
        <div style={{ ...modalOverlayStyle, zIndex: 1001 }}>
          <div style={{ ...modalContainerBase, maxWidth: '500px' }}>
            <h3 style={{ color: '#e5e7eb', marginBottom: '16px' }}>Release löschen</h3>
            <p style={{ color: '#9ca3af', marginBottom: '24px' }}>
              Möchten Sie wirklich das Release <strong style={{ color: '#e5e7eb' }}>{deleteTarget.version}</strong> ({deleteTarget.channel}) löschen?
              Alle Dateien und Daten werden entfernt.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteTarget(null)
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#374151',
                  color: '#e5e7eb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#991b1b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <Trash2 size={16} />
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Download Modal */}
      {showEditModal && (
        <div style={{ ...modalOverlayStyle, zIndex: 1001 }}>
          <div style={{ ...modalContainerBase, maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#e5e7eb', margin: 0 }}>
                Download bearbeiten - {editData.version} ({editData.channel})
              </h3>
              <button onClick={closeEditModal} style={modalCloseButtonStyle}>
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="edit-version" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                Version
              </label>
              <input
                id="edit-version"
                type="text"
                value={editData.version}
                disabled
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="edit-filename" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                Dateiname
              </label>
              <input
                id="edit-filename"
                type="text"
                value={editData.filename}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="edit-channel" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                Channel *
              </label>
              <select
                id="edit-channel"
                value={editData.channel}
                onChange={(e) => setEditData({ ...editData, channel: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e5e7eb'
                }}
              >
                <option value="stable">Stable</option>
                <option value="beta">Beta</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label htmlFor="edit-os" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Betriebssystem *
                </label>
                <select
                  id="edit-os"
                  value={editData.os}
                  onChange={(e) => setEditData({ ...editData, os: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb'
                  }}
                >
                  <option value="windows">Windows</option>
                  <option value="macos">macOS</option>
                  <option value="linux">Linux</option>
                </select>
              </div>

              <div>
                <label htmlFor="edit-arch" style={{ display: 'block', color: '#9ca3af', marginBottom: '8px', fontSize: '14px' }}>
                  Architektur *
                </label>
                <select
                  id="edit-arch"
                  value={editData.arch}
                  onChange={(e) => setEditData({ ...editData, arch: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e5e7eb'
                  }}
                >
                  <option value="x64">x64</option>
                  <option value="arm64">ARM64</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeEditModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#374151',
                  color: '#e5e7eb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUpdateDownload}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#1e40af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                <CheckCircle size={16} />
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}

ProductReleases.propTypes = {
  productSlug: PropTypes.string.isRequired
}

export default DownloadAdministrationContent
